<template>
  <svg :width="props.width" :height="height" class="bg-white/10" ref="svgcanvas">
    <g v-for="region in regionsCreated" :key="region.id">
      <circle
        :cx="region.x"
        :cy="region.y"
        :r="region.r"
        :style="{ fill: colorScale(region.value) }"
        class="cursor-pointer hover:brightness-110 hover:stroke-black/50 hover:stroke-2"
        @mouseover="onMouseover($event, region)"
        @mouseout="onMouseout($event, region)"
        @click="onClick($event, region)"
      />
      <foreignObject
        :x="region.x - 40"
        :y="region.y - 12"
        :width="80"
        :height="50"
        text-anchor="middle"
        font-size="12px"
        class="font-bold pointer-events-none"
      >
        <div
          class="text-center leading-tight"
          :style="{ color: textScale(region.value) }"
        >
          {{ region.name }}
        </div>
      </foreignObject>
    </g>
  </svg>
  <ClientOnly>
    <Teleport :to="teleportSelector" :enabled="true">
      <div class="flex lg:flex-row flex-col gap-8 justify-between">
        <div class="flex flex-row justify-start max-w-3xl mt-2 gap-3">
          <div
            v-for="(c, i) in legendRanges"
            :key="i"
            class="flex flex-row items-center gap-2"
          >
            <span
              class="circle"
              :style="{ 'background-color': colorScale(c[1]) }"
            >
            </span>
            <span class="text-xs text-center text-Shades-SoftBlack">
              {{ $formater.salaryFormater(c[0]) }}-{{
                $formater.salaryFormater(c[1])
              }}
            </span>
          </div>
        </div>
        <div>
          <div class="flex items-center">
            <div class="w-2 h-2 rounded-full flex-shrink-0 bg-Primary-200"></div>
            <div class="w-4 h-4 rounded-full flex-shrink-0 bg-Primary-400 ml-2"></div>
            <div class="w-6 h-6 rounded-full flex-shrink-0 bg-Primary-600 ml-2"></div>
            <span class="ml-2 text-xs flex-shrink">= Global and regional programming amounts </span>
          </div>
        </div>
      </div>
    </Teleport>
  </ClientOnly>
  <div class="download-block flex flex-row gap-2 justify-end mt-4">
    <button @click="downloadSVG" class="text-xs hover:text-Primary-600 flex items-center gap-1"> <ArrowDownTrayIcon class="w-4 h-4" /> SVG</button> | 
    <button @click="downloadPNG" class="text-xs hover:text-Primary-600 flex items-center gap-1"> <ArrowDownTrayIcon class="w-4 h-4" /> PNG</button>
  </div>
</template>

<script setup>
import * as d3 from "d3";
import forceBoundary from "d3-force-boundary";
import { ref, onMounted } from "vue";
import { ArrowDownTrayIcon } from "@heroicons/vue/20/solid";
import SvgExport from "svg-export-forkmulti";
import { Canvg, presets } from "canvg";
import Session from "svg-text-to-path";
const props = defineProps({
  // Any props can go here
  colorRange: {
    type: Array,
    default: () => ["#f7fbff", "#08306b"],
  },
  width: {
    type: Number,
    default: 800,
  },
  regions: {
    type: Array,
    default: () => [],
  },
  teleportSelector: {
    type: String,
    default: "",
  },
});

const emits = defineEmits(["_mouseover", "_mouseout", "_click"]);

// Template ref for SVG element
const svgcanvas = ref(null);

const onMouseover = (event, region) => {
  emits("_mouseover", {
    value: region.value,
    name: region.name,
    x: event.pageX,
    y: event.pageY,
  });
};

const onMouseout = (event, region) => {
  emits("_mouseout", {
    value: region.name,
    name: region.value,
  });
};

const onClick = (event, region) => {
  emits("_click", {
    value: region.value,
    name: region.name,
    recipient_code: region.recipient_code,
  });
};

const height = computed(() => 700);

const regions = computed(() => {
  if (props.regions.length > 0) {
    return props.regions;
  } else
    return [
      // {
      //   name: "Africa",
      //   recipient_code: 298,
      //   centroid: { lon: 17.5375, lat: -8.7832 },
      //   value: 919.902846,
      // },
      // {
      //   name: "America",
      //   recipient_code: 498,
      //   centroid: { lon: -100.0, lat: 39.8283 },
      //   value: 152.193319,
      // },
      // {
      //   name: "Asia",
      //   recipient_code: 798,
      //   centroid: { lon: 77.209, lat: 35.9078 },
      //   value: 715.729945,
      // },

      // {
      //   name: "Caribbean",
      //   recipient_code: 1031,
      //   centroid: { lon: -73.5949, lat: 12.1165 },
      //   value: 16.833294,
      // },
      // {
      //   name: "Central America",
      //   recipient_code: 1032,
      //   centroid: { lon: -85.6024, lat: 12.7781 },
      //   value: 5.886876,
      // },
      // {
      //   name: "Central Asia",
      //   recipient_code: 619,
      //   centroid: { lon: 63.0, lat: 41.0 },
      //   value: 84.851003,
      // },
      // {
      //   name: "Eastern Africa",
      //   recipient_code: 1027,
      //   centroid: { lon: 36.8219, lat: -6.369 },
      //   value: 10.211028,
      // },
      {
        name: "Europe",
        recipient_code: 89,
        centroid: { lon: 9.5559, lat: 48.0196 },
        value: 959.899671,
      },
      {
        name: "Far East Asia",
        recipient_code: 789,
        centroid: { lon: 143.2042, lat: 44.8488 },
        value: 31.698178,
      },
      {
        name: "Middle Africa",
        recipient_code: 1028,
        centroid: { lon: 18.6667, lat: -3.5 },
        value: 12.890477,
      },
      {
        name: "Middle East",
        recipient_code: 589,
        centroid: { lon: 45.0792, lat: 23.6345 },
        value: 832.935857,
      },
      {
        name: "North of Sahara",
        recipient_code: 189,
        centroid: { lon: 15.6355, lat: 25.2637 },
        value: 118.185684,
      },
      {
        name: "Oceania",
        recipient_code: 889,
        centroid: { lon: 134.491, lat: -25.7329 },
        value: 15.489108,
      },
      {
        name: "South & Central Asia",
        recipient_code: 689,
        centroid: { lon: 73.8567, lat: 22.5726 },
        value: 26.264143,
      },
      {
        name: "South America",
        recipient_code: 489,
        centroid: { lon: -60.9789, lat: -14.235 },
        value: 626.924111,
      },
      {
        name: "South Asia",
        recipient_code: 679,
        centroid: { lon: 78.9629, lat: 20.5937 },
        value: 74.293873,
      },
      {
        name: "South of Sahara",
        recipient_code: 289,
        centroid: { lon: 18.4297, lat: -13.1339 },
        value: 681.528674,
      },
      {
        name: "Southern Africa",
        recipient_code: 1029,
        centroid: { lon: 25.0839, lat: -29.0 },
        value: 0.878135,
      },
      {
        name: "Western Africa",
        recipient_code: 1030,
        centroid: { lon: -1.8113, lat: 6.8666 },
        value: 18.98068,
      },
    ];
});

const projection = computed(() => {
  const projectionRatio = 0.7;
  return d3
    .geoMercator()
    .translate([props.width / 2, height.value / 2])
    .scale(props.width / 4);
});
const regionsCreated = ref([]);

function initRegions() {
  regionsCreated.value = regions.value.map((region) => {
    return {
      ...region,
      initCentroid: projection.value([
        region.centroid.lon,
        region.centroid.lat,
      ]),
      r: scale.value(region.value),
      x: projection.value([region.centroid.lon, region.centroid.lat])[0],
      y: projection.value([region.centroid.lon, region.centroid.lat])[1],
    };
  });
}

watch(
  () => props.regions,
  (newValue, oldValue) => {
    initRegions();
    nextTick(() => {
      const simulation = d3
        .forceSimulation()
        .nodes(regionsCreated.value)
        .force("boundary", forceBoundary(0, 0, props.width, height.value))
        .force(
          "collision",
          d3
            .forceCollide()
            .radius((d) =>
              d.r + 24 > MAXRADIUS + 12 ? MAXRADIUS + 12 : d.r + 24
            )
        );

      // .force("x", d3.forceX(props.width / 2).strength(0.115))
      // .force("y", d3.forceY(height.value / 2).strength(0.115))
      // add force to keep the circles within the canvas
    });
  }
);

const MAXRADIUS = 80;

const scale = computed(() => {
  return d3
    .scaleSqrt()
    .domain([0, d3.max(regions.value, (d) => d.value)])
    .range([0, MAXRADIUS]);
});

// a linear scale between the min and max of the color range
const colorScale = computed(() => {
  return d3
    .scaleLinear()
    .domain([0, d3.max(regions.value, (d) => d.value)])
    .range(props.colorRange);
});

const textScale = computed(() => {
  return d3
    .scaleQuantize()
    .domain([0, d3.max(regions.value, (d) => d.value)])
    .range(["black", "white"]);
});
onMounted(() => {
  initRegions();
  nextTick(() => {
    const simulation = d3
      .forceSimulation()
      .nodes(regionsCreated.value)
      .force(
        "boundary",
        forceBoundary(0, 0, props.width, height.value)
          .hardBoundary(true)
          .strength(0.02)
      )
      .force(
        "collision",
        d3
          .forceCollide()
          .radius((d) =>
            d.r + 24 > MAXRADIUS + 12 ? MAXRADIUS + 12 : d.r + 24
          )
      );
    // .force("x", d3.forceX([props.width / 2]).strength(0.015))
    // .force("y", d3.forceY([height.value / 2]).strength(0.015));
    //.force("charge", d3.forceManyBody().strength(100))
  });
});

// legend ranges
const legendRanges = computed(() => {
  const ranges = [];
  const steps = 6;
  // get the ranges from the color scale
  const stepsArray = d3.range(
    0,
    d3.max(regions.value, (d) => d.value) + 1, // To produce a range with an inclusive upper bound (i.e., that includes the end value), a common trick is to use a slightly larger end that is still less than end + step.
    d3.max(regions.value, (d) => d.value) / steps
  );

  for (let i = 0; i < stepsArray.length - 1; i++) {
    const range = [stepsArray[i], stepsArray[i + 1]];
    ranges.push(range);
  }
  return ranges;
});

const downloadSVG = () => {
  const svg = d3.select(svgcanvas.value).node();
  const exporter = new SvgExport({
    textToPath: Session,
  });
  exporter.downloadSvg(svg, "regions-map", {
    convertTextToPath: true,
  });
};

const downloadPNG = () => {
  const exporter = new SvgExport({
    canvg: Canvg,
    presets: presets,
  });
  const svg = d3.select(svgcanvas.value).node();
  exporter.downloadPng(svg, "regions-map", {
    scale: 4,
  });
};
</script>

<style scoped>
/* Any additional styles can go here */
span.circle {
  display: inline-block;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  vertical-align: top;
}
</style>

<!-- COMMANDS START
The commands used in this implementation are:
- `d3.geoMercator()` (https://github.com/d3/d3-geo#geoMercator)
- `d3.scaleSqrt()` (https://github.com/d3/d3-scale#scaleSqrt)
- `ref` (https://v3.vuejs.org/api/refs-api.html#ref)
- `onMounted` (https://v3.vuejs.org/api/composition-api.html#onmounted)
 COMMANDS END -->
